import { AlertTriangle, ArrowLeft, Clock, Package, PackagePlus, Pencil } from 'lucide-react'
import { parseAsStringLiteral, useQueryState } from 'nuqs'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import type { ProductWithStock, Stock } from '@snootsvet/shared'
import { clinicIdSchema, formatQuantity, pluralizeUnit, requireClinic } from '@snootsvet/shared'

import { ClinicSearchWithDefault } from '@/frontend/components/clinic-search'
import { FormattedCalendarDate } from '@/frontend/components/formatted-datetime'
import { Button } from '@/frontend/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/frontend/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/frontend/components/ui/tooltip'
import { StockRowForm } from '@/frontend/features/inventory/stock-row.form'
import { useGetAuthenticatedStaff } from '@/frontend/hooks/api/use-get-authenticated-staff'
import { useGetProduct } from '@/frontend/hooks/api/use-get-product'
import { useDocumentTitle } from '@/frontend/hooks/use-document-title'
import {
  formatPackBreakdown,
  getStockRowStatus,
  hasBatchedStock,
  isExpiringSoon,
  stockRowTextClass,
} from '@/frontend/lib/stock'
import { cn } from '@/frontend/lib/utils'
import { routes } from '@/frontend/routes'

/**
 * Per-product stock management page with clinic filtering and superuser
 * corrections that bypass the intake audit trail.
 */
export function ProductStockPage() {
  const { data: authenticatedStaff } = useGetAuthenticatedStaff()
  const isSuperuser = authenticatedStaff?.isSuperuser === true
  const { productId } = useParams<{ productId: string }>()
  const { data: product, isLoading, error } = useGetProduct(productId, { include: 'stock' })

  useDocumentTitle(product?.name)

  const [clinicFilter, setClinicFilter] = useQueryState(
    'clinic',
    parseAsStringLiteral(clinicIdSchema.options)
  )
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<Stock | null>(null)

  if (isLoading) return <div>Loading...</div>
  if (error || !product) return <div>Error loading product.</div>

  // Cross-clinic signal - the practice as a whole moves to batch tracking,
  // and some clinics lag behind, so any unbatched row should get flagged when
  // the product is batched anywhere else.
  const productIsBatched = hasBatchedStock(product.stock)

  const filteredStock = clinicFilter
    ? product.stock.filter((s) => s.clinicId === clinicFilter)
    : product.stock

  // Sort: by clinic name, then rows with stock first, then unbatched rows
  // first within each (so null-batch rows surface for cleanup when the product
  // is usually batch-tracked), then expiry date descending.
  const sortedStock = [...filteredStock].sort((a, b) => {
    const clinicDiff = requireClinic(a.clinicId).name.localeCompare(requireClinic(b.clinicId).name)
    if (clinicDiff !== 0) return clinicDiff
    const stockDiff = Number(b.quantity > 0) - Number(a.quantity > 0)
    if (stockDiff !== 0) return stockDiff
    const batchDiff = Number(a.batchNumber === null) - Number(b.batchNumber === null)
    if (batchDiff !== 0) return batchDiff
    const aExp = a.expiryDate ? new Date(a.expiryDate).getTime() : Number.NEGATIVE_INFINITY
    const bExp = b.expiryDate ? new Date(b.expiryDate).getTime() : Number.NEGATIVE_INFINITY
    return bExp - aExp
  })

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Link
          to={routes.inventory}
          className="mb-2 inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back to inventory
        </Link>
        <h1 className="text-headline-lg">{product.name}</h1>
      </div>

      {/* Filter + primary action */}
      <div className="mb-4 flex items-center gap-3">
        <ClinicSearchWithDefault
          includeArchived
          id="filter-clinic"
          value={clinicFilter}
          onChange={setClinicFilter}
          placeholder="All Clinics"
          allowClear
        />
        {isSuperuser && (
          <Button size="lg" className="ml-auto" onClick={() => setCreateOpen(true)}>
            <PackagePlus />
            Add Stock
          </Button>
        )}
      </div>

      {/* Stock table */}
      {sortedStock.length === 0 ? (
        <p className="py-8 text-center text-muted-foreground">
          No stock rows{clinicFilter ? ' for this clinic' : ''}.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Clinic</TableHead>
              <TableHead>Batch number</TableHead>
              <TableHead className="text-right">Quantity</TableHead>
              <TableHead>Expiry</TableHead>
              <TableHead>Status</TableHead>
              {isSuperuser && <TableHead className="w-12" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedStock.map((row) => (
              <StockRow
                key={row.id}
                stock={row}
                product={product}
                flagUnbatched={row.batchNumber === null && productIsBatched}
                onEdit={isSuperuser ? () => setEditing(row) : undefined}
              />
            ))}
          </TableBody>
        </Table>
      )}

      {/* Create dialog */}
      <Dialog open={isSuperuser && createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add stock row</DialogTitle>
          </DialogHeader>
          <StockRowForm
            productId={product.id}
            defaultClinicId={clinicFilter}
            onSuccess={() => setCreateOpen(false)}
            onCancel={() => setCreateOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog
        open={isSuperuser && editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null)
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit stock row</DialogTitle>
          </DialogHeader>
          {editing && (
            <StockRowForm
              productId={product.id}
              existing={editing}
              onSuccess={() => setEditing(null)}
              onCancel={() => setEditing(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

/** Status label shown in the row's status cell. */
const STATUS_LABEL = {
  valid: 'In stock',
  depleted: 'Depleted',
  expired: 'Expired',
  expired_depleted: 'Expired & depleted',
  // Not used on this page — we pass hasBatchedStock: false to getStockRowStatus,
  // so unbatched rows are flagged via a separate column indicator instead.
  unbatched_mixed: 'Unbatched',
} as const

function StockRow({
  stock,
  product,
  flagUnbatched,
  onEdit,
}: {
  stock: Stock
  product: ProductWithStock
  /** Show a warning indicator: this row has no batch number but the product is
   *  usually batch-tracked somewhere in the practice. */
  flagUnbatched: boolean
  onEdit?: () => void
}) {
  const breakdown = formatPackBreakdown(stock.quantity, product)
  // Skip 'unbatched_mixed' classification here — we surface that case via a
  // dedicated AlertTriangle on the batch-number cell, not via the row status.
  const status = getStockRowStatus(stock, { hasBatchedStock: false })
  const textClass = stockRowTextClass(status)
  const clinic = requireClinic(stock.clinicId)
  const isExpired = status === 'expired' || status === 'expired_depleted'
  const expiringSoon = !isExpired && isExpiringSoon(stock.expiryDate)

  return (
    <TableRow>
      <TableCell>{clinic.name}</TableCell>
      <TableCell className="font-mono">
        {stock.batchNumber ?? (
          <span
            className={cn(
              'inline-flex items-center gap-1.5',
              flagUnbatched ? 'text-warning-fg' : 'text-muted-foreground'
            )}
          >
            {flagUnbatched && (
              <Tooltip>
                <TooltipTrigger render={<AlertTriangle className="size-3.5" />} />
                <TooltipContent className="text-sm">
                  This product is usually batch-tracked at Snoots. Consider doing a stock count and
                  replacing this row with properly batched stock.
                </TooltipContent>
              </Tooltip>
            )}
            No batch number
          </span>
        )}
      </TableCell>
      <TableCell className={cn('text-right tabular-nums', textClass)}>
        <div className="font-medium">
          {formatQuantity(stock.quantity)} {pluralizeUnit(product.unit, stock.quantity)}
        </div>
        {breakdown && (
          <div className="text-sm font-normal italic text-muted-foreground">({breakdown})</div>
        )}
      </TableCell>
      <TableCell
        className={cn(
          isExpired ? textClass : expiringSoon ? 'text-warning-fg' : 'text-muted-foreground'
        )}
      >
        {stock.expiryDate ? (
          <span className="flex items-center gap-1">
            {(isExpired || expiringSoon) && <Clock className="size-3.5" />}
            <FormattedCalendarDate value={stock.expiryDate} />
          </span>
        ) : (
          '\u2014'
        )}
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-1">
          <span className={cn('flex items-center gap-1.5', textClass)}>
            {isExpired ? <Clock className="size-4" /> : <Package className="size-4" />}
            {STATUS_LABEL[status]}
          </span>
          {expiringSoon && (
            <span className="flex items-center gap-1.5 text-warning-fg">
              <Clock className="size-4" />
              Expiring soon
            </span>
          )}
        </div>
      </TableCell>
      {onEdit && (
        <TableCell>
          <Button variant="ghost" size="icon" onClick={onEdit} aria-label="Edit stock row">
            <Pencil className="size-4 text-muted-foreground" />
          </Button>
        </TableCell>
      )}
    </TableRow>
  )
}
